import { useParams } from "react-router-dom";
import { useContracts } from "../useHook";
import { useEffect, useState, useMemo, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Paragraph from "@tiptap/extension-paragraph";
import Heading from "@tiptap/extension-heading";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import {
  Table,
  TableRow,
  TableHeader,
  TableCell,
} from "@tiptap/extension-table";
import { Mark, mergeAttributes, Node } from "@tiptap/core";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Save,
  Download,
  Edit3,
  Type,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import Swal from "sweetalert2";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

const PAGE_PREVIEW_CYCLE_PX = 1151;

/** Read text-align from an inline style string (Word / editor). */
function getTextAlignFromStyle(style) {
  if (!style || typeof style !== "string") return null;
  const m = style.match(/text-align\s*:\s*([^;]+)/i);
  return m ? m[1].trim().toLowerCase() : null;
}

/** Set or remove text-align in style while keeping other declarations. */
function mergeTextAlignIntoStyle(style, textAlign) {
  const raw = (style || "").trim();
  let noAlign = raw
    .replace(/text-align\s*:\s*[^;]+;?/gi, "")
    .replace(/;+/g, ";")
    .trim();
  noAlign = noAlign.replace(/^;|;$/g, "").trim();
  if (!textAlign || textAlign === "left") {
    return noAlign || null;
  }
  const piece = `text-align: ${textAlign}`;
  if (!noAlign) return `${piece};`;
  return `${noAlign.replace(/;?\s*$/, "")}; ${piece};`;
}

/** Effective alignment for toolbar state (LTR). */
function normalizeBlockTextAlign(style) {
  const raw = getTextAlignFromStyle(style);
  if (!raw) return "left";
  const v = raw.toLowerCase();
  if (v === "start") return "left";
  if (v === "end") return "right";
  return v;
}

function wrapSheetsForEditor(html) {
  if (!html || html.includes("contract-docx-page-sheet")) return html || "";
  const re = /(<div\b[^>]*\bdata-type="docx-page-break"[^>]*><\/div>)/gi;
  const chunks = html.split(re);
  while (chunks.length > 2 && !(chunks[0] || "").trim()) {
    chunks.splice(0, 2);
  }
  const bodyParts = [];
  for (let i = 0; i < chunks.length; i += 2) {
    bodyParts.push(chunks[i] || "");
  }
  if (bodyParts.length === 0) return "";
  const wrapSheet = (inner, pageIndex) =>
    `<div class="contract-docx-page-sheet" data-page-index="${pageIndex}">${inner}</div>`;
  if (bodyParts.length === 1) return wrapSheet(bodyParts[0], 1);
  let out = "";
  for (let i = 0; i < bodyParts.length; i++) {
    out += wrapSheet(bodyParts[i], i + 1);
    if (i < bodyParts.length - 1) out += chunks[i * 2 + 1] || "";
  }
  return out;
}

function unwrapSheetsFromEditor(html) {
  if (!html || !html.includes("contract-docx-page-sheet")) return html || "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("div.contract-docx-page-sheet").forEach((sheet) => {
    while (sheet.firstChild) {
      sheet.parentNode.insertBefore(sheet.firstChild, sheet);
    }
    sheet.remove();
  });
  return doc.body.innerHTML;
}

// Custom Mark for Editable Fields (Better for wrapping selected text)
const EditableField = Mark.create({
  name: "editableField",
  addAttributes() {
    return {
      id: { default: null },
      placeholder: { default: "Type here..." },
      required: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute("data-required") === "true",
        renderHTML: (attributes) => {
          if (!attributes.required) return {};
          return { "data-required": "true" };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-type="editable-field"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    const isRequired = HTMLAttributes["data-required"] === "true";
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-type": "editable-field",
        class: `editable-field-template ${isRequired ? "required-field" : ""}`,
      }),
      0,
    ];
  },
  addCommands() {
    return {
      toggleEditableField:
        (attributes) =>
        ({ commands }) => {
          return commands.toggleMark(this.name, attributes);
        },
    };
  },
});

/** Keeps inline styles/classes from Word (alignment, spacing). */
const StyledParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) =>
          attributes.class ? { class: attributes.class } : {},
      },
    };
  },
});

const StyledHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) =>
          attributes.class ? { class: attributes.class } : {},
      },
    };
  },
}).configure({ levels: [1, 2, 3, 4] });

const PreservingTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) =>
          attributes.class ? { class: attributes.class } : {},
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
    };
  },
});

/** Keeps cell background / alignment from Word footer tables */
const PreservingTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
    };
  },
});

/** Preserves Word page breaks injected at upload (server → HTML → editor). */
const ContractPageBreak = Node.create({
  name: "contractPageBreak",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      breakSource: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-break-source"),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="docx-page-break"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    const { breakSource, ...rest } = HTMLAttributes;
    const isWord = breakSource === "word";
    return [
      "div",
      mergeAttributes(
        { "data-type": "docx-page-break", contenteditable: "false" },
        rest,
        {
          class: `contract-docx-page-break${isWord ? " contract-docx-soft-page-break" : ""}`,
          ...(breakSource ? { "data-break-source": breakSource } : {}),
        },
      ),
    ];
  },
});

const ContractPageSheet = Node.create({
  name: "contractPageSheet",
  group: "block",
  content: "block+",
  defining: true,
  isolating: true,
  parseHTML() {
    return [{ tag: "div.contract-docx-page-sheet" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: "contract-docx-page-sheet" }),
      0,
    ];
  },
});

const ContractsDetails = () => {
  const { id } = useParams();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission(SystemPermissions.Contract.UPDATE);
  const { getContractById, updateContract } = useContracts();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approxPages, setApproxPages] = useState(1);
  const [stripPaperHeight, setStripPaperHeight] = useState(0);
  const [sheetLabelTops, setSheetLabelTops] = useState([]);
  const [description, setDescription] = useState("");
  const editorRef = useRef(null);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        paragraph: false,
        heading: false,
      }),
      StyledParagraph,
      StyledHeading,
      Image.configure({
        allowBase64: true,
        inline: false,
      }),
      Underline,
      PreservingTable.configure({ resizable: false }),
      TableRow,
      TableHeader,
      PreservingTableCell,
      ContractPageSheet,
      ContractPageBreak,
      EditableField,
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: "",
    editable: canUpdate,
    editorProps: {
      attributes: {
        class:
          "contract-tiptap-root contract-page-stripes mx-auto focus:outline-none max-w-none min-h-[297mm] w-[210mm] p-0 text-[15px] leading-[1.75] text-gray-800 shadow-2xl border border-gray-200 print:shadow-none print:border-none print:p-0 print:w-full",
      },
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(canUpdate);
    }
  }, [editor, canUpdate]);

  useEffect(() => {
    setLoading(true);
    if (id) {
      getContractById(id)
        .then((res) => {
          setContract(res);
          setDescription(res?.description || "");
          if (editor && res.content) {
            editor.commands.setContent(wrapSheetsForEditor(res.content));
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [id, editor]);

  // Approximate page count from editor height (A4-ish); does not draw overlays on text.
  useEffect(() => {
    if (!editorRef.current || loading) return;

    const updatePages = () => {
      const editorElement = editorRef.current?.querySelector(".ProseMirror");
      if (!editorElement) return;
      const height = editorElement.scrollHeight;
      setStripPaperHeight(height);
      const sheets = editorElement.querySelectorAll(
        ".contract-docx-page-sheet",
      );
      if (sheets.length > 0) {
        setApproxPages(sheets.length);
        const rootRect = editorElement.getBoundingClientRect();
        setSheetLabelTops(
          Array.from(sheets).map((s) => {
            const r = s.getBoundingClientRect();
            return r.top - rootRect.top + editorElement.scrollTop;
          }),
        );
      } else {
        setSheetLabelTops([]);
        setApproxPages(Math.max(1, Math.ceil(height / PAGE_PREVIEW_CYCLE_PX)));
      }
    };

    const observer = new ResizeObserver(() => updatePages());
    const editorElement = editorRef.current.querySelector(".ProseMirror");
    if (editorElement) observer.observe(editorElement);
    updatePages();
    return () => observer.disconnect();
  }, [editor, loading]);

  const extractEditableFields = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const fieldNodes = doc.querySelectorAll('span[data-type="editable-field"]');

    return Array.from(fieldNodes).map((node, index) => ({
      id: node.getAttribute("id") || `field_${index}`,
      name: node.textContent.trim(),
      placeholder: node.getAttribute("placeholder") || "Type here...",
      required: node.getAttribute("data-required") === "true",
    }));
  };

  const handleSave = async () => {
    const editorHtml = editor.getHTML();
    const content = unwrapSheetsFromEditor(editorHtml);
    const fields = extractEditableFields(content);
    const pm = editorRef.current?.querySelector(".ProseMirror");
    const docxBreaks = (content.match(/data-type="docx-page-break"/g) || [])
      .length;
    const sheetPages =
      pm?.querySelectorAll(".contract-docx-page-sheet").length ?? 0;
    const mergedPageCount = Math.max(
      docxBreaks + 1,
      sheetPages,
      approxPages,
      contract?.page_count || 1,
    );

    try {
      await updateContract(id, {
        description,
        content,
        fields,
        page_count: mergedPageCount,
      });
      Swal.fire({
        icon: "success",
        title: "Template Saved",
        text: `Template updated with ${fields.length} editable fields.`,
        timer: 2000,
        showConfirmButton: false,
        background: "#fff",
        color: "#111",
        iconColor: "#4f46e5",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: "There was an error saving the contract template.",
      });
    }
  };

  const markAsEditable = (isRequired = false) => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    if (from === to) {
      Swal.fire(
        "Info",
        "Highlight text that the end-user should be able to edit on the website.",
        "info",
      );
      return;
    }
    // Use the explicit command from our Mark extension
    editor.chain().focus().toggleEditableField({ required: isRequired }).run();
  };

  const downloadAsPDF = () => {
    window.print();
  };

  /** @param {"left" | "center" | "right"} align */
  const setBlockTextAlign = (align) => {
    if (!editor) return;
    const { $from } = editor.state.selection;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name !== "paragraph" && node.type.name !== "heading")
        continue;
      const pos = $from.before(depth);
      const stored = align === "left" ? null : align;
      const newStyle = mergeTextAlignIntoStyle(node.attrs.style, stored);
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, style: newStyle });
          return true;
        })
        .run();
      return;
    }
  };

  /** @param {"left" | "center" | "right"} align */
  const blockTextAlignIs = (align) => {
    if (!editor) return false;
    const { $from } = editor.state.selection;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "paragraph" || node.type.name === "heading") {
        return normalizeBlockTextAlign(node.attrs.style) === align;
      }
    }
    return false;
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#f3f4f6] min-h-screen">
      {/* Admin Dashboard Toolbar */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm p-4 print:hidden">
        <div className="container mx-auto px-4 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-800">
              <Edit3 className="text-indigo-600" size={24} />
              <h1 className="text-xl font-bold">
                {canUpdate
                  ? "Contract Template Editor"
                  : "Contract Template Viewer"}
              </h1>
            </div>
            {canUpdate && (
              <>
                <div className="h-6 w-[1px] bg-gray-300 mx-2" />
                <div className="flex items-center gap-1.5 bg-gray-50 p-1 rounded-lg border border-gray-100">
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor?.isActive("bold")}
                    icon={<Bold size={18} />}
                    title="Bold"
                  />
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor?.isActive("italic")}
                    icon={<Italic size={18} />}
                    title="Italic"
                  />
                  <ToolbarButton
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 1 }).run()
                    }
                    active={editor?.isActive("heading", { level: 1 })}
                    icon={<Heading1 size={18} />}
                    title="H1"
                  />
                  <ToolbarButton
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                    active={editor?.isActive("heading", { level: 2 })}
                    icon={<Heading2 size={18} />}
                    title="H2"
                  />
                  <div className="h-6 w-[1px] bg-gray-200 mx-1" />
                  <ToolbarButton
                    onClick={() => setBlockTextAlign("left")}
                    active={blockTextAlignIs("left")}
                    icon={<AlignLeft size={18} />}
                    title="Align left"
                  />
                  <ToolbarButton
                    onClick={() => setBlockTextAlign("center")}
                    active={blockTextAlignIs("center")}
                    icon={<AlignCenter size={18} />}
                    title="Align center"
                  />
                  <ToolbarButton
                    onClick={() => setBlockTextAlign("right")}
                    active={blockTextAlignIs("right")}
                    icon={<AlignRight size={18} />}
                    title="Align right"
                  />
                  <div className="h-6 w-[1px] bg-gray-200 mx-1" />
                  <ToolbarButton
                    onClick={() =>
                      editor.chain().focus().toggleBulletList().run()
                    }
                    active={editor?.isActive("bulletList")}
                    icon={<List size={18} />}
                    title="Bullets"
                  />
                  <ToolbarButton
                    onClick={() =>
                      editor.chain().focus().toggleOrderedList().run()
                    }
                    active={editor?.isActive("orderedList")}
                    icon={<ListOrdered size={18} />}
                    title="Numbered List"
                  />
                  <div className="h-6 w-[1px] bg-gray-200 mx-1" />
                  <button
                    onClick={() => markAsEditable(false)}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-md transition-all font-bold text-xs uppercase tracking-wider shadow-sm ${
                      editor?.isActive("editableField", { required: false })
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-indigo-600 border border-indigo-100 hover:bg-indigo-50"
                    }`}
                    title="Optional Field"
                  >
                    <Type size={16} />
                    Editable For User
                  </button>
                  <button
                    onClick={() => markAsEditable(true)}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-md transition-all font-bold text-xs uppercase tracking-wider shadow-sm ${
                      editor?.isActive("editableField", { required: true })
                        ? "bg-orange-600 text-white"
                        : "bg-white text-orange-600 border border-orange-100 hover:bg-orange-50"
                    }`}
                    title="Mandatory Field"
                  >
                    <Type size={16} />
                    Required For User
                  </button>
                </div>
              </>
            )}
          </div>

          {canUpdate && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-all font-semibold flex items-center gap-2 shadow-md"
              >
                <Save size={18} />
                Save Template
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-center py-12 px-4 print:py-0 overflow-y-auto">
        <div className="max-w-full">
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between print:hidden">
            <div className="flex items-center gap-4">
              <div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Document Title
                </span>
                <h2 className="text-lg font-bold text-gray-700">
                  {contract?.title}
                </h2>
              </div>
              {/* <div className="h-10 w-[1px] bg-gray-200 mx-2" />
                            <div className="min-w-[280px]">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Description</span>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Add a short description"
                                    className="mt-1 w-full min-h-[54px] resize-y rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                />
                            </div> */}
              {contract?.page_count && (
                <div className="h-10 w-[1px] bg-gray-200 mx-2" />
              )}
              {contract?.page_count && (
                <div>
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Original Pages
                  </span>
                  <div className="flex items-center gap-1.5 text-gray-500 font-bold">
                    <div className="bg-gray-50 px-2 py-0.5 rounded text-sm">
                      {contract?.page_count} Pages
                    </div>
                  </div>
                </div>
              )}
              <div className="h-10 w-[1px] bg-gray-200 mx-2" />
              <div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Est. Pages (layout)
                </span>
                <div className="flex items-center gap-1.5 text-indigo-700 font-bold">
                  <div className="bg-indigo-50 px-2 py-0.5 rounded text-sm">
                    {approxPages} Pages
                  </div>
                </div>
              </div>
            </div>
            <div className="text-right flex flex-col gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Legend
              </span>
              <div className="flex gap-4">
                <span className="text-[11px] font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">
                  Blue = Editable
                </span>
                <span className="text-[11px] font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100">
                  Orange with * = Required
                </span>
              </div>
            </div>
          </div>
          <div
            className="relative paper-canvas overflow-visible"
            ref={editorRef}
          >
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>

      <style>{`
                .ProseMirror {
                    font-family: 'Inter', sans-serif;
                    line-height: 1.8;
                    color: #1f2937;
                    outline: none !important;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
                }

                @media screen {
                    .ProseMirror.contract-page-stripes {
                        position: relative;
                        background-color: #e2e8f0;
                    }
                    .ProseMirror.contract-page-stripes:not(:has(.contract-docx-page-sheet)) {
                        background-image: linear-gradient(
                            to bottom,
                            #ffffff 0,
                            #ffffff 1116px,
                            #64748b 1116px,
                            #64748b 1122px,
                            #e2e8f0 1122px,
                            #e2e8f0 1151px
                        );
                        background-size: 100% 1151px;
                        background-repeat: repeat-y;
                        background-position: 0 0;
                    }
                }
                .ProseMirror .contract-docx-page-sheet {
                    background: #ffffff;
                    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
                    margin-bottom: 1.25rem;
                    padding: 20mm;
                    box-sizing: border-box;
                }
                .ProseMirror .contract-docx-page-sheet:last-child {
                    margin-bottom: 0;
                }
                .ProseMirror .contract-docx-page-sheet + .contract-docx-page-break {
                    display: none !important;
                    height: 0 !important;
                    margin: 0 !important;
                    border: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                }

                .ProseMirror strong, .ProseMirror b { font-weight: 700; }
                .ProseMirror em, .ProseMirror i { font-style: italic; }
                .ProseMirror u { text-decoration: underline; }
                .ProseMirror ol {
                    list-style-type: decimal !important;
                    list-style-position: outside !important;
                    padding-left: 1.75rem !important;
                    margin: 0.5rem 0 0.75rem !important;
                }
                .ProseMirror ul {
                    list-style-type: disc !important;
                    list-style-position: outside !important;
                    padding-left: 1.75rem !important;
                    margin: 0.5rem 0 0.75rem !important;
                }
                .ProseMirror li {
                    display: list-item !important;
                    margin: 0.25rem 0;
                }
                .ProseMirror li > p { margin-bottom: 0.35rem; }
                .ProseMirror table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
                .ProseMirror th, .ProseMirror td { border: 1px solid #e5e7eb; padding: 0.35rem 0.5rem; vertical-align: top; }
                .ProseMirror table.contract-docx-table { margin: 0; }
                .ProseMirror table.contract-docx-table th,
                .ProseMirror table.contract-docx-table td { border: none; }

                .ProseMirror sup { vertical-align: super; font-size: 0.75em; }
                .ProseMirror sub { vertical-align: sub; font-size: 0.75em; }

                .ProseMirror p[style*="text-align:center"],
                .ProseMirror p[style*="text-align: center"],
                .ProseMirror h1[style*="text-align:center"],
                .ProseMirror h1[style*="text-align: center"],
                .ProseMirror h2[style*="text-align:center"],
                .ProseMirror h2[style*="text-align: center"],
                .ProseMirror h3[style*="text-align:center"],
                .ProseMirror h3[style*="text-align: center"],
                .ProseMirror h4[style*="text-align:center"],
                .ProseMirror h4[style*="text-align: center"] { text-align: center !important; }
                .ProseMirror p[style*="text-align:right"],
                .ProseMirror p[style*="text-align: right"],
                .ProseMirror h1[style*="text-align:right"],
                .ProseMirror h1[style*="text-align: right"],
                .ProseMirror h2[style*="text-align:right"],
                .ProseMirror h2[style*="text-align: right"],
                .ProseMirror h3[style*="text-align:right"],
                .ProseMirror h3[style*="text-align: right"],
                .ProseMirror h4[style*="text-align:right"],
                .ProseMirror h4[style*="text-align: right"] { text-align: right !important; }

                .ProseMirror .contract-docx-part img {
                    max-width: 100%;
                    height: auto;
                    display: block;
                    margin-left: auto;
                    margin-right: auto;
                }
                .ProseMirror .contract-docx-header { margin-bottom: 0.75rem; }
                .ProseMirror .contract-docx-header img {
                    max-height: 56px !important;
                    max-width: 100% !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain !important;
                }
                .ProseMirror .contract-docx-footer img {
                    max-height: 48px !important;
                    max-width: 100% !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain !important;
                }
                .ProseMirror .contract-docx-footer { margin-top: 1rem; }

                /* Some Word headers/footers render logos inside tables.
                   Those images may not be descendants of .contract-docx-header, so clamp them too. */
                .ProseMirror .contract-docx-header-table img {
                    max-height: 56px !important;
                    max-width: 100% !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain !important;
                }
                .ProseMirror .contract-docx-footer-table img {
                    max-height: 48px !important;
                    max-width: 100% !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain !important;
                }

                /* Final fallback: if header/footer markup doesn't carry the exact
                   class name we expect, still clamp any logo-like images. */
                .ProseMirror [class*="contract-docx-header"] img {
                    max-height: 56px !important;
                    max-width: 100% !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain !important;
                }
                .ProseMirror [class*="contract-docx-footer"] img {
                    max-height: 48px !important;
                    max-width: 100% !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain !important;
                }

                .contract-docx-page-break {
                    clear: both;
                    height: 0;
                    margin: 1.25rem 0;
                    border: none;
                    border-top: 1px dashed #cbd5e1;
                    position: relative;
                }
                .contract-docx-page-break::before {
                    content: 'Word page break';
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 9px;
                    font-weight: 700;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    color: #94a3b8;
                    background: #fff;
                    padding: 0 8px;
                }

                .paper-canvas {
                    background-color: #f3f4f6;
                    padding: 40px;
                    border-radius: 12px;
                }

                /* Ensure Word lists/numbering render (Tailwind preflight can hide markers) */
                .ProseMirror ul {
                    list-style-type: disc;
                    padding-left: 1.5rem;
                    margin: 0.75rem 0;
                }
                .ProseMirror ol {
                    list-style-type: decimal;
                    padding-left: 1.5rem;
                    margin: 0.75rem 0;
                }
                .ProseMirror li {
                    margin: 0.25rem 0;
                }
                .ProseMirror ul ul { list-style-type: circle; }
                .ProseMirror ol ol { list-style-type: lower-alpha; }
                
                /* Selection styling */
                .ProseMirror ::selection {
                    background: rgba(79, 70, 229, 0.2);
                }

                .ProseMirror h1 {
                    font-weight: 800;
                    color: #111827;
                    margin-top: 1.25rem;
                    margin-bottom: 0.75rem;
                    font-size: 1.5rem;
                }
                .ProseMirror h2 { font-size: 1.25rem; font-weight: 700; margin: 0.75rem 0 0.5rem; }
                .ProseMirror h3 { font-size: 1.1rem; font-weight: 600; margin: 0.5rem 0 0.35rem; }
                .ProseMirror h4 { font-size: 1rem; font-weight: 600; margin: 0.5rem 0 0.25rem; }

                .editable-field-template {
                    background-color: rgba(79, 70, 229, 0.1);
                    border-bottom: 2px dashed #4f46e5;
                    border-radius: 4px;
                    padding: 2px 6px;
                    color: #4338ca;
                    font-weight: 600;
                    position: relative;
                }

                .editable-field-template.required-field {
                    background-color: rgba(234, 88, 12, 0.1);
                    border-bottom: 2px dashed #ea580c;
                    color: #c2410c;
                }

                .editable-field-template::after {
                    content: 'EDITABLE';
                    position: absolute;
                    top: -14px;
                    right: 0;
                    font-size: 8px;
                    background: #4f46e5;
                    color: white;
                    padding: 1px 4px;
                    border-radius: 3px;
                    font-weight: 900;
                }

                .editable-field-template.required-field::after {
                    content: 'REQUIRED *';
                    background: #ea580c;
                }

                @media print {
                    .ProseMirror { 
                        box-shadow: none !important; 
                        border: none !important; 
                        width: 100% !important; 
                        padding: 0 !important;
                        min-height: auto !important;
                        background: white !important;
                        background-image: none !important;
                    }
                    .ProseMirror .contract-docx-page-sheet {
                        box-shadow: none !important;
                        margin-bottom: 0 !important;
                        page-break-after: always;
                        break-after: page;
                    }
                    .ProseMirror .contract-docx-page-sheet:last-child {
                        page-break-after: auto;
                        break-after: auto;
                    }
                    .ProseMirror .contract-docx-page-sheet + .contract-docx-page-break {
                        display: none !important;
                        page-break-before: auto !important;
                    }
                    .contract-docx-page-break {
                        border-top: none !important;
                        margin: 0 !important;
                        page-break-before: always;
                        break-before: page;
                    }
                    .contract-docx-page-break::before { display: none !important; }
                    .editable-field-template { 
                        background: transparent !important; 
                        border: none !important;
                        padding: 0 !important;
                        color: black !important;
                    }
                    .editable-field-template::after { display: none !important; }
                }
            `}</style>
    </div>
  );
};

const ToolbarButton = ({ onClick, active, icon, title }) => (
  <button
    onClick={onClick}
    title={title}
    className={`p-2 rounded-md transition-all ${
      active
        ? "bg-indigo-100 text-indigo-700"
        : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
    }`}
  >
    {icon}
  </button>
);

export default ContractsDetails;
